import {
  AlignmentType,
  BorderStyle,
  Document,
  ExternalHyperlink,
  Footer,
  Header,
  ImageRun,
  PageNumber,
  PageOrientation,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  Tab,
  TabStopType,
  TextRun,
  VerticalAlign,
  WidthType,
  type ISectionOptions,
} from "docx";
import type { Align, DocumentLayout, HeaderFooterLine, LayoutBlock, ParagraphBlock, Run, TableBlock } from "./pdfLayout";

/** A picture to place on a page. Position is in points from the top-left of the displayed page. */
export interface ImageBlock {
  kind: "image";
  data: Uint8Array;
  type: "jpg" | "png";
  widthPt: number;
  heightPt: number;
  topPt: number;
  leftPt: number;
  rightPt: number;
  description: string;
}

export type OutBlock = LayoutBlock | ImageBlock;

export interface OutPage {
  widthPt: number;
  heightPt: number;
  marginsPt: { top: number; right: number; bottom: number; left: number };
  blocks: OutBlock[];
}

const tw = (pt: number) => Math.max(0, Math.round(pt * 20));
const PX_PER_PT = 96 / 72;

const DEVANAGARI = /[ऀ-ॿ]/;
// eslint-disable-next-line no-misleading-character-class -- intentionally spans the astral private-use planes too
const PRIVATE_USE = new RegExp("[\\uE000-\\uF8FF\\u{F0000}-\\u{FFFFD}\\u{100000}-\\u{10FFFD}]", "gu");
// Characters XML cannot carry (Word refuses the file): control characters other than tab / newline.
// eslint-disable-next-line no-control-regex
const XML_BAD = /[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g;

/** Text safe for Word, and the font it needs (Devanagari needs a font that has it). */
export function sanitizeText(text: string): { text: string; font?: string } {
  const t = text.replace(PRIVATE_USE, "•").replace(XML_BAD, "");
  return { text: t, font: DEVANAGARI.test(t) ? "Nirmala UI" : undefined };
}

const hexOk = (c?: string) => (c && /^[0-9a-f]{6}$/i.test(c) ? c.toUpperCase() : undefined);

function textRunFor(run: Run, text: string, overrides?: { color?: string; underline?: boolean }): TextRun {
  const s = sanitizeText(text);
  const parts = s.text.split("\t");
  const children: (string | Tab)[] = [];
  parts.forEach((p, i) => {
    if (i > 0) children.push(new Tab());
    if (p) children.push(p);
  });
  return new TextRun({
    children,
    bold: run.bold || undefined,
    italics: run.italic || undefined,
    size: Math.max(2, Math.round(run.sizePt * 2)),
    color: overrides?.color ?? hexOk(run.color),
    font: s.font ?? run.font,
    underline: overrides?.underline ? {} : undefined,
  });
}

export function runsToChildren(runs: Run[]): (TextRun | ExternalHyperlink)[] {
  const out: (TextRun | ExternalHyperlink)[] = [];
  for (const run of runs) {
    if (run.field) {
      out.push(
        new TextRun({
          children: [run.field === "PAGE" ? PageNumber.CURRENT : PageNumber.TOTAL_PAGES],
          bold: run.bold || undefined,
          italics: run.italic || undefined,
          size: Math.max(2, Math.round(run.sizePt * 2)),
          color: hexOk(run.color),
          font: run.font,
        }),
      );
    } else if (run.link) {
      out.push(
        new ExternalHyperlink({
          link: run.link,
          children: [textRunFor(run, run.text, { color: hexOk(run.color) ?? "0563C1", underline: true })],
        }),
      );
    } else if (run.text) out.push(textRunFor(run, run.text));
  }
  return out;
}

const ALIGN: Record<Align, (typeof AlignmentType)[keyof typeof AlignmentType]> = {
  left: AlignmentType.LEFT,
  center: AlignmentType.CENTER,
  right: AlignmentType.RIGHT,
};

function paragraphFor(b: ParagraphBlock, opts: { pageBreakBefore?: boolean } = {}): Paragraph {
  return new Paragraph({
    children: runsToChildren(b.runs),
    alignment: b.align ? ALIGN[b.align] : undefined,
    indent: b.indentPt > 0 ? { left: tw(b.indentPt) } : undefined,
    bullet: b.bullet ? { level: b.bullet.level } : undefined,
    spacing: { after: tw(b.spaceAfterPt) },
    pageBreakBefore: opts.pageBreakBefore || undefined,
  });
}

const GRID = { style: BorderStyle.SINGLE, size: 4, color: "BFBFBF" };

function tableFor(b: TableBlock, contentWidthTw: number): Table {
  const natural = b.colWidthsPt.map(tw);
  const total = natural.reduce((a, c) => a + c, 0) || 1;
  const scale = Math.min(1, contentWidthTw / total);
  const widths = natural.map((w) => Math.max(240, Math.round(w * scale)));
  return new Table({
    width: { size: widths.reduce((a, c) => a + c, 0), type: WidthType.DXA },
    columnWidths: widths,
    layout: TableLayoutType.FIXED,
    borders: { top: GRID, bottom: GRID, left: GRID, right: GRID, insideHorizontal: GRID, insideVertical: GRID },
    rows: b.rows.map(
      (row) =>
        new TableRow({
          cantSplit: true,
          children: row.map(
            (cell, ci) =>
              new TableCell({
                width: { size: widths[ci], type: WidthType.DXA },
                verticalAlign: VerticalAlign.TOP,
                children: [new Paragraph({ children: runsToChildren(cell.runs), alignment: ALIGN[b.colAlign[ci] ?? "left"] })],
              }),
          ),
        }),
    ),
  });
}

function imageParagraph(b: ImageBlock, pageWidthPt: number, contentWidthPt: number, opts: { pageBreakBefore?: boolean } = {}): Paragraph {
  const scale = Math.min(1, contentWidthPt / Math.max(1, b.widthPt));
  const w = Math.max(1, Math.round(b.widthPt * scale * PX_PER_PT));
  const h = Math.max(1, Math.round(b.heightPt * scale * PX_PER_PT));
  const centred = Math.abs((b.leftPt + b.rightPt) / 2 - pageWidthPt / 2) < 12 && b.leftPt > 24;
  return new Paragraph({
    alignment: centred ? AlignmentType.CENTER : undefined,
    pageBreakBefore: opts.pageBreakBefore || undefined,
    children: [
      new ImageRun({
        type: b.type,
        data: b.data,
        transformation: { width: w, height: h },
        altText: { name: "Image", description: b.description, title: b.description },
      }),
    ],
  });
}

/** The small paragraph that must follow a table (Word needs one), or carries a page break. */
const spacer = (pageBreakBefore = false) =>
  new Paragraph({ run: { size: 2 }, spacing: { before: 0, after: 80, line: 20 }, pageBreakBefore: pageBreakBefore || undefined });

function headerFooterParagraph(line: HeaderFooterLine, contentWidthTw: number): Paragraph {
  const children: (TextRun | ExternalHyperlink)[] = [];
  const tabStops: { type: (typeof TabStopType)[keyof typeof TabStopType]; position: number }[] = [];
  const parts = line.parts;
  if (parts.length === 2) tabStops.push({ type: TabStopType.RIGHT, position: contentWidthTw });
  if (parts.length === 3) {
    tabStops.push({ type: TabStopType.CENTER, position: Math.round(contentWidthTw / 2) });
    tabStops.push({ type: TabStopType.RIGHT, position: contentWidthTw });
  }
  parts.forEach((runs, i) => {
    if (i > 0) children.push(new TextRun({ children: [new Tab()] }));
    children.push(...runsToChildren(runs));
  });
  return new Paragraph({ children, alignment: parts.length === 1 ? ALIGN[line.align] : undefined, tabStops: tabStops.length ? tabStops : undefined });
}

const sizeKey = (p: OutPage) => `${Math.round(p.widthPt / 2)}x${Math.round(p.heightPt / 2)}`;

/**
 * Puts pictures among the blocks of a page in reading order: an image goes before the first block
 * (in its own column) that starts below it; with no block alongside it, by height alone.
 */
export function placeImages(blocks: LayoutBlock[], images: ImageBlock[]): OutBlock[] {
  const out: OutBlock[] = [...blocks];
  const sorted = [...images].sort((a, b) => a.topPt - b.topPt);
  for (const img of sorted) {
    const overlaps = (b: OutBlock) => {
      const ov = Math.min(b.rightPt, img.rightPt) - Math.max(b.leftPt, img.leftPt);
      return ov >= 0.3 * Math.min(b.rightPt - b.leftPt, img.rightPt - img.leftPt);
    };
    let idx = -1;
    let lastAlongside = -1;
    for (let i = 0; i < out.length; i++) {
      const b = out[i];
      if (!overlaps(b)) continue;
      lastAlongside = i;
      if (b.topPt > img.topPt) {
        idx = i;
        break;
      }
    }
    if (idx < 0) {
      if (lastAlongside >= 0) idx = lastAlongside + 1;
      else {
        idx = out.findIndex((b) => b.topPt > img.topPt);
        if (idx < 0) idx = out.length;
      }
    }
    out.splice(idx, 0, img);
  }
  return out;
}

export interface BuildOptions {
  pages: OutPage[];
  header?: HeaderFooterLine[];
  footer?: HeaderFooterLine[];
  title?: string;
  author?: string;
}

const median = (v: number[]) => [...v].sort((a, b) => a - b)[Math.floor(v.length / 2)] ?? 0;

/** Builds the Word document: one section per run of equal-sized pages, so Letter, A4 and landscape pages keep their own size. */
export function buildDocxDocument(opts: BuildOptions): Document {
  const runs: OutPage[][] = [];
  for (const p of opts.pages) {
    const last = runs[runs.length - 1];
    if (last && sizeKey(last[0]) === sizeKey(p)) last.push(p);
    else runs.push([p]);
  }

  const sections: ISectionOptions[] = runs.map((group, gi) => {
    const first = group[0];
    const landscape = first.widthPt > first.heightPt;
    const margin = {
      top: median(group.map((p) => p.marginsPt.top)),
      bottom: median(group.map((p) => p.marginsPt.bottom)),
      left: median(group.map((p) => p.marginsPt.left)),
      right: median(group.map((p) => p.marginsPt.right)),
    };
    const contentWidthPt = Math.max(72, first.widthPt - margin.left - margin.right);
    const contentWidthTw = tw(contentWidthPt);
    const children: (Paragraph | Table)[] = [];

    group.forEach((page, pi) => {
      const needBreak = pi > 0; // the first page of a section already starts on a new page
      const blocks = page.blocks;
      if (!blocks.length) {
        children.push(spacer(needBreak));
        return;
      }
      let breakPending = needBreak;
      blocks.forEach((b, bi) => {
        const nextIsTable = blocks[bi + 1]?.kind === "table";
        if (b.kind === "paragraph") {
          children.push(paragraphFor(b, { pageBreakBefore: breakPending }));
        } else if (b.kind === "table") {
          if (breakPending) children.push(spacer(true));
          children.push(tableFor(b, contentWidthTw));
          if (bi === blocks.length - 1 || nextIsTable || blocks[bi + 1]?.kind === "image") children.push(spacer());
        } else {
          children.push(imageParagraph(b, page.widthPt, contentWidthPt, { pageBreakBefore: breakPending }));
        }
        breakPending = false;
      });
    });
    if (children[children.length - 1] instanceof Table) children.push(spacer());

    const headers = opts.header?.length ? { default: new Header({ children: opts.header.map((l) => headerFooterParagraph(l, contentWidthTw)) }) } : undefined;
    const footers = opts.footer?.length ? { default: new Footer({ children: opts.footer.map((l) => headerFooterParagraph(l, contentWidthTw)) }) } : undefined;
    const section: ISectionOptions = {
      headers,
      footers,
      properties: {
        page: {
          size: landscape
            ? { width: tw(Math.min(first.widthPt, first.heightPt)), height: tw(Math.max(first.widthPt, first.heightPt)), orientation: PageOrientation.LANDSCAPE }
            : { width: tw(first.widthPt), height: tw(first.heightPt) },
          margin: { top: tw(margin.top), bottom: tw(margin.bottom), left: tw(margin.left), right: tw(margin.right), header: 360, footer: 360 },
        },
      },
      children,
    };
    return section;
  });

  return new Document({
    creator: opts.author || "FreeDailyPro",
    lastModifiedBy: "FreeDailyPro",
    title: opts.title,
    description: "Converted from PDF with FreeDailyPro",
    sections,
  });
}

/** Convenience for tests and callers that only have a DocumentLayout and no images. */
export function pagesFromLayout(layout: DocumentLayout): OutPage[] {
  return layout.pages.map((p) => ({ widthPt: p.widthPt, heightPt: p.heightPt, marginsPt: p.marginsPt, blocks: p.blocks }));
}
